import { Box } from 'components/Box';
import { AnimateSharedLayout, motion } from 'framer-motion';
import React, { useMemo, useState } from 'react';
import { useInterval } from 'src/helpers/useInterval';
import { uuidv4 } from 'src/helpers/uuidv4';
import SwiperCore, { Autoplay } from 'swiper';
import 'swiper/swiper-bundle.css';
import { Heading } from '../Heading';

SwiperCore.use([Autoplay]);

const ListItem = ({ item, isSelected }) => {
    return (
        <Box
            as="li"
            p="spacing-lg"
            mb="spacing"
            bg="scrollCard"
            rounded="radius"
        >
            <Heading kind="h5" mb={0}>
                {item}
            </Heading>
        </Box>
    );
};

export const ScrollingList = ({ items, height = 400, ...props }: any) => {
    return (
        <Box h={height} overflow="hidden">
            <AnimatedList items={items} height={height} />
        </Box>
    );
};

function AnimatedList({ items, height }) {
    // duplicate the list if the list is short to avoid any issues when transitioning the last items of the list
    // and add ids to use as keys and layout ids
    const listWithId = useMemo(
        () =>
            (items?.length <= 6 ? items.concat(items) : items).map(item => ({
                ...item,
                id: uuidv4(),
            })),
        [items]
    );
    const [listItems, setListItems] = useState(listWithId);

    const updateList = async () => {
        const current = listItems[0];
        setListItems(prev => {
            const prevList = [...prev];
            prevList.shift();

            return prevList;
        });

        setListItems(prev => {
            const prevList = [...prev];
            // we don't want to show the transition from the first item getting pushed to the bottom
            // so push it after removing from top of list, rather than at the same time
            prevList.push(current);

            return prevList;
        });
    };

    useInterval(
        () => {
            updateList();
        },
        // Delay in milliseconds or null to stop it
        1500
    );

    return (
        <Box h="100%" transform="translateY(-82px)">
            <AnimateSharedLayout>
                <motion.ul layout>
                    {listItems.map((listItem, index) => {
                        if (!listItem) {
                            return null;
                        }

                        const opacityIndex = Math.abs(1 - index);
                        return (
                            <motion.div
                                layoutId={listItem.id}
                                key={listItem.id}
                                initial={{
                                    opacity: 0,
                                }}
                                animate={{ opacity: 1 - opacityIndex * 0.2 }}
                            >
                                <ListItem
                                    item={listItem.title}
                                    isSelected={index === 1}
                                />
                            </motion.div>
                        );
                    })}
                </motion.ul>
            </AnimateSharedLayout>
        </Box>
    );
}
